import { Bar, BarChart, ResponsiveContainer, Tooltip, type TooltipProps, XAxis, YAxis } from 'recharts';
import { planStatusLabels } from '~/common/projectUtils';
import { PlanStatus } from '~/common/types';
import type { ChartData } from './useChartData';

interface StackedBarChartProps {
  data: ChartData[];
  segments: string[];
  title?: string;
  'data-testid'?: string;
}

const formatTitleCase = (str: string | number | null | undefined) => {
  if (typeof str !== 'string' || !str) return str;
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// colors for chart segmentation
const STATUS_COLORS = {
  [PlanStatus.Scheduled]: '#16A34A',
  [PlanStatus.NeedsAttention]: '#F97316',
  [PlanStatus.Pending]: '#94A3B8',
} as const;

export default function StackedBarChart({ data, segments, title, 'data-testid': dataTestId }: StackedBarChartProps) {
  const getColor = (segment: string) => STATUS_COLORS[segment as keyof typeof STATUS_COLORS];

  return (
    <div className="h-full w-full" data-testid={dataTestId}>
      {title && (
        <div className="text-xs font-medium" data-testid={dataTestId ? `${dataTestId}-title` : undefined}>
          {title}
        </div>
      )}

      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 0, left: 0, bottom: 10 }}>
          <XAxis
            type="number"
            fontSize={12}
            tick={{ fill: '#6B7280' }}
            axisLine={{ stroke: '#E5E7EB' }}
            orientation="bottom"
            tickLine={false}
            tickFormatter={(value) => `${Math.round(value / 5280)}`}
            label={{
              value: 'Gas Distribution Length (miles)',
              position: 'outsideBottom',
              fontSize: '10',
              dy: 10,
            }}
          />
          <YAxis
            type="category"
            dataKey="axis"
            fontSize={11}
            axisLine={true}
            tickLine={false}
            tickFormatter={(value) => (typeof value === 'string' ? formatTitleCase(value) : value)}
          />
          <Tooltip cursor={{ fill: 'transparent' }} content={<CustomTooltip />} />

          {segments.map((segment) => (
            <Bar key={segment} dataKey={segment} stackId="stack" fill={getColor(segment)} name={segment} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (!active || !payload) return null;

  return (
    <div className="border bg-white p-3">
      <p className="font-medium">{formatTitleCase(label)}</p>
      {payload.map((entry, index) => {
        const displayLabel = planStatusLabels[entry.name as keyof typeof planStatusLabels] ?? entry.name;

        return (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: legacy
            key={`tooltip-${index}`}
            className="flex items-center gap-2"
          >
            <div className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-xs">
              {displayLabel}: {entry.value ? `${(entry.value / 5280).toFixed(2)} miles` : '0 miles'}
            </span>
          </div>
        );
      })}
    </div>
  );
};
